import { billingModelLabel } from '../format';
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const W = { bg:'#0A1526', surface:'#16233F', border:'#28395C', muted:'#7d8ea2', bright:'#f1f5f9', accent:'#00A0C8', danger:'#fc8181', warn:'#f6ad55', success:'#48bb78' };
const inp = { padding:'7px 10px', borderRadius:'6px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'13px', boxSizing:'border-box', outline:'none' };
const lbl = { fontSize:'11px', color:W.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' };

const PERIOD_OPTIONS = [
  { id:'3m',  label:'Last 3 Months' },
  { id:'6m',  label:'Last 6 Months' },
  { id:'12m', label:'Last 12 Months' },
  { id:'ytd', label:'Year to Date' },
];

function periodRange(id) {
  const now   = new Date();
  const today = now.toISOString().slice(0, 10);
  if (id === 'ytd') {
    return { start: `${now.getFullYear()}-01-01`, end: today };
  }
  const months = id === '3m' ? 3 : id === '6m' ? 6 : 12;
  const d = new Date(now);
  d.setMonth(d.getMonth() - months);
  return { start: d.toISOString().slice(0, 10), end: today };
}

function fmtRate(r) { return r != null ? `$${parseFloat(r).toFixed(0)}/hr` : '—'; }
function fmtMoney(v) { return v != null ? `$${Math.round(v).toLocaleString()}` : '—'; }

export default function RateReviewWizard({ org, clients, onClose, onRefresh }) {
  const STEPS = ['Select Period', 'Review & Edit Rates', 'Save Changes'];

  const [step, setStep]         = useState(0);
  const [periodId, setPeriodId] = useState('3m');
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  // Rate changes now open a new billing period rather than overwriting the
  // client's current rate, so the batch needs an effective date. Defaults to
  // the 1st of next month, which is what a rate review usually means.
  const [effectiveFrom, setEffectiveFrom] = useState(() => {
    const d = new Date();
    return d.getMonth() === 11
      ? `${d.getFullYear() + 1}-01-01`
      : `${d.getFullYear()}-${String(d.getMonth() + 2).padStart(2, '0')}-01`;
  });
  const [done, setDone]         = useState(false);

  // { clientId: hours }
  const [hoursMap, setHoursMap] = useState({});
  // { clientId: { rate, retainer, overage } } — editable draft
  const [edits, setEdits] = useState({});
  // clientId -> effective_from of the period in force today. How long a rate has
  // stood is the whole point of a rate review, and billing terms finally make it
  // knowable -- before this table there was no record of when a rate last moved.
  const [rateSince, setRateSince] = useState({});

  const billableClients = (clients || []).filter(c => c.is_active !== false && !c.is_internal);

  const fetchHours = async () => {
    setLoading(true);
    setErr('');
    const { start, end } = periodRange(periodId);
    const { data, error } = await supabase
      .from('time_entries')
      .select('client_id, hours')
      .eq('org_id', org.id)
      .gte('entry_date', start)
      .lt('entry_date', end);
    setLoading(false);
    if (error) { setErr(error.message); return; }
    const map = {};
    (data || []).forEach(e => {
      if (e.client_id) map[e.client_id] = (map[e.client_id] || 0) + parseFloat(e.hours || 0);
    });
    setHoursMap(map);

    const today = new Date().toISOString().slice(0, 10);
    const { data: terms } = await supabase
      .from('client_billing_terms')
      .select('client_id, effective_from, effective_to')
      .eq('org_id', org.id)
      .lte('effective_from', today);
    const since = {};
    (terms || []).forEach(t => {
      if (t.effective_to && t.effective_to <= today) return;   // already ended
      const cur = since[t.client_id];
      if (!cur || t.effective_from > cur) since[t.client_id] = t.effective_from;
    });
    setRateSince(since);

    // Seed edits from current client rates
    const draft = {};
    billableClients.forEach(c => {
      draft[c.id] = { rate: c.rate ?? '', retainer: c.retainer ?? '', overage: c.overage ?? '' };
    });
    setEdits(draft);
  };

  useEffect(() => {
    if (step === 1) fetchHours();
  }, [step, periodId]); // eslint-disable-line

  const setEdit = (clientId, field, value) => {
    setEdits(e => ({ ...e, [clientId]: { ...e[clientId], [field]: value } }));
  };

  const changed = billableClients.filter(c => {
    const e = edits[c.id] || {};
    return (
      parseFloat(e.rate || 0)     !== parseFloat(c.rate     || 0) ||
      parseFloat(e.retainer || 0) !== parseFloat(c.retainer || 0) ||
      parseFloat(e.overage || 0)  !== parseFloat(c.overage  || 0)
    );
  });

  const saveChanges = async () => {
    if (changed.length === 0) { onClose(); return; }
    setErr('');
    setSaving(true);
    for (const c of changed) {
      const e = edits[c.id] || {};
      // Opens a new billing period. Writing clients.* directly would leave no
      // record that the old rate ever applied, and would put the cache out of
      // step with the terms history.
      const { error } = await supabase.rpc('change_client_billing_terms', {
        p_client_id: c.id,
        p_effective_from: effectiveFrom,
        p_model: c.model,
        p_config: {
          rate:     parseFloat(e.rate     || 0),
          retainer: parseFloat(e.retainer || 0),
          overage:  parseFloat(e.overage  || 0),
          hours:    parseFloat(c.hours    || 0),
        },
        p_note: 'Rate review',
      });
      if (error) { setErr(`Failed to update ${c.name}: ${error.message}`); setSaving(false); return; }
    }
    setSaving(false);
    onRefresh();
    setDone(true);
  };

  const defaultRate = parseFloat(org.default_hourly_rate || 0);

  // Months the current rate has been in force, and whether that is long enough
  // to be worth a look on its own.
  const monthsSince = (iso) => {
    if (!iso) return null;
    const [y, m] = iso.split('-').map(Number);
    const now = new Date();
    return (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  };
  const STALE_MONTHS = 12;

  const effectiveRate = (c) => {
    const hrs = hoursMap[c.id] || 0;
    if (hrs === 0) return null;
    if (c.model === 'hourly')   return parseFloat(edits[c.id]?.rate || c.rate || 0);
    if (c.model === 'retainer') return parseFloat(edits[c.id]?.retainer || c.retainer || 0) / hrs;
    if (c.model === 'hybrid') {
      const base  = parseFloat(edits[c.id]?.retainer || c.retainer || 0);
      const incl  = parseFloat(c.hours || 0);
      const ov    = parseFloat(edits[c.id]?.overage  || c.overage  || 0);
      return (base + Math.max(0, hrs - incl) * ov) / hrs;
    }
    return null;
  };

  const clientsWithHours = billableClients.filter(c => hoursMap[c.id] > 0);

  // Three of five production orgs have no default_hourly_rate set, which made
  // this whole screen a passive table: the flag was gated on `defaultRate > 0`,
  // so nothing was ever flagged and a client billing at $39/hr looked the same
  // as one at $300/hr. Fall back to the median effective rate across the org's
  // own clients, which needs no configuration to be useful.
  const benchmark = (() => {
    if (defaultRate > 0) return { value: defaultRate, label: `your org default rate of $${defaultRate}/hr` };
    const ers = clientsWithHours.map(effectiveRate).filter(v => v != null && v > 0).sort((a, b) => a - b);
    if (ers.length < 3) return null;
    const mid = Math.floor(ers.length / 2);
    const med = ers.length % 2 ? ers[mid] : (ers[mid - 1] + ers[mid]) / 2;
    // Below the median is half your clients by definition -- too noisy to flag.
    // Only call out the ones meaningfully adrift of it.
    return { value: med * 0.7, label: `70% of your median effective rate ($${Math.round(med)}/hr)`, median: med };
  })();

  const isUnderpriced = (c) => {
    const er = effectiveRate(c);
    return er !== null && benchmark !== null && er < benchmark.value;
  };
  const isStale = (c) => {
    const n = monthsSince(rateSince[c.id]);
    return n !== null && n >= STALE_MONTHS;
  };
  const flagged = clientsWithHours.filter(isUnderpriced);
  const stale   = clientsWithHours.filter(c => isStale(c) && !isUnderpriced(c));

  const stepContent = [
    <div key="period">
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {PERIOD_OPTIONS.map(o => {
          const active = periodId === o.id;
          return (
            <div key={o.id} onClick={() => setPeriodId(o.id)}
              style={{ padding:'14px 16px', borderRadius:'9px', cursor:'pointer', display:'flex', alignItems:'center', gap:'12px', transition:'border-color 0.15s,background 0.15s',
                border:`1px solid ${active ? W.accent : W.border}`,
                background: active ? 'rgba(0,160,200,0.08)' : W.bg }}>
              <div style={{ width:'16px', height:'16px', borderRadius:'50%', flexShrink:0, transition:'all 0.15s',
                border:`2px solid ${active ? W.accent : '#3a4558'}`,
                background: active ? W.accent : 'transparent' }} />
              <span style={{ fontSize:'14px', fontWeight:'600', color:W.bright }}>{o.label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize:'12px', color:W.muted, marginTop:'14px', lineHeight:'1.5' }}>
        Pulls all time entries in the period to calculate effective rates, and flags clients billing well below the rest — plus any rate that has not moved in over a year.
      </div>
    </div>,

    <div key="review">
      {loading && <div style={{ color:W.muted, fontSize:'13px', padding:'30px', textAlign:'center' }}>Loading data…</div>}
      {!loading && (
        <>
          {flagged.length > 0 && (
            <div style={{ padding:'10px 14px', borderRadius:'8px', background:'rgba(246,173,85,0.08)', border:`1px solid rgba(246,173,85,0.3)`, marginBottom:'8px', fontSize:'12px', color:W.warn }}>
              ⚠️ {flagged.length} client{flagged.length !== 1 ? 's are' : ' is'} billing below {benchmark.label}.
              {defaultRate <= 0 && (
                <span style={{ display:'block', marginTop:'4px', color:W.muted }}>
                  No default hourly rate is set for this organization, so your own clients are the yardstick.
                  Set one in Settings → Organization to compare against a number you choose instead.
                </span>
              )}
            </div>
          )}
          {stale.length > 0 && (
            <div style={{ padding:'10px 14px', borderRadius:'8px', background:'rgba(49,167,217,0.07)', border:`1px solid rgba(49,167,217,0.22)`, marginBottom:'14px', fontSize:'12px', color:W.accent }}>
              🕑 {stale.length} client{stale.length !== 1 ? 's have' : ' has'} not had a rate change in over a year.
            </div>
          )}
          {flagged.length === 0 && stale.length === 0 && clientsWithHours.length > 0 && (
            <div style={{ padding:'10px 14px', borderRadius:'8px', background:'rgba(104,211,145,0.07)', border:`1px solid rgba(104,211,145,0.22)`, marginBottom:'14px', fontSize:'12px', color:W.muted }}>
              Nothing stands out — no client is billing well below the others, and every rate has moved within the last year.
              Edit any rate below anyway if you want to change it.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
            {/* Header */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 80px 80px 80px', gap:'8px', padding:'6px 10px', borderBottom:`1px solid ${W.border}` }}>
              {['Client','Hours','Rate','Add\'l','Eff. Rate'].map(h => (
                <div key={h} style={{ fontSize:'10px', fontWeight:'700', color:W.muted, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</div>
              ))}
            </div>
            {billableClients.length === 0 && (
              <div style={{ color:W.muted, fontSize:'13px', padding:'20px', textAlign:'center' }}>No billable clients.</div>
            )}
            {billableClients.map(c => {
              const hrs = hoursMap[c.id] || 0;
              const er  = effectiveRate(c);
              const flagThis = isUnderpriced(c);
              const staleThis = isStale(c);
              const ageMonths = monthsSince(rateSince[c.id]);
              const e   = edits[c.id] || {};
              return (
                <div key={c.id} style={{ display:'grid', gridTemplateColumns:'1fr 60px 80px 80px 80px', gap:'8px', padding:'10px 10px', borderBottom:`1px solid ${W.border}`, alignItems:'center',
                  background: flagThis ? 'rgba(246,173,85,0.04)' : 'transparent' }}>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright }}>
                      {flagThis && <span style={{ color:W.warn, marginRight:'4px' }}>⚠</span>}
                      {c.name}
                    </div>
                    <div style={{ fontSize:'10px', color:W.muted }}>
                      <span>{billingModelLabel(c.model)}</span>
                      {ageMonths !== null && (
                        <span style={{ color: staleThis ? W.accent : W.muted }}>
                          {' · '}
                          {ageMonths < 1 ? 'rate set this month' : `same rate ${ageMonths} mo${ageMonths === 1 ? '' : 's'}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize:'13px', color: hrs > 0 ? W.bright : W.muted }}>{hrs > 0 ? `${Math.round(hrs * 10) / 10}h` : '—'}</div>
                  {/* Primary rate field */}
                  <input
                    style={{ ...inp, width:'100%', fontSize:'12px', padding:'5px 8px' }}
                    type="number" min="0" step="0.01"
                    title={c.model === 'hourly' ? 'Hourly rate' : 'Monthly base'}
                    value={c.model === 'hourly' ? (e.rate ?? '') : (e.retainer ?? '')}
                    onChange={ev => setEdit(c.id, c.model === 'hourly' ? 'rate' : 'retainer', ev.target.value)}
                  />
                  {/* Overage field (hybrid only) */}
                  {c.model === 'hybrid'
                    ? <input style={{ ...inp, width:'100%', fontSize:'12px', padding:'5px 8px' }} type="number" min="0" step="0.01"
                        title="Overage rate ($/hr)" value={e.overage ?? ''}
                        onChange={ev => setEdit(c.id, 'overage', ev.target.value)} />
                    : <div />
                  }
                  <div style={{ fontSize:'12px', color: flagThis ? W.warn : W.muted }}>
                    {er !== null ? `$${Math.round(er)}/hr` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
          {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'10px' }}>{err}</div>}
        </>
      )}
    </div>,

    <div key="confirm">
      {changed.length === 0 ? (
        <div style={{ textAlign:'center', padding:'20px 0' }}>
          <div style={{ fontSize:'32px', marginBottom:'10px' }}>✓</div>
          <div style={{ fontSize:'15px', fontWeight:'700', color:W.bright, marginBottom:'6px' }}>No changes to save</div>
          <div style={{ fontSize:'13px', color:W.muted }}>All rates match their current values.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize:'13px', color:W.muted, marginBottom:'12px' }}>{changed.length} rate{changed.length !== 1 ? 's' : ''} will be updated:</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'1px', marginBottom:'14px' }}>
            {changed.map(c => {
              const e = edits[c.id] || {};
              const field = c.model === 'hourly' ? 'rate' : 'retainer';
              const oldVal = parseFloat(c[field] || 0);
              const newVal = parseFloat(e[field] || 0);
              return (
                <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', borderRadius:'7px', border:`1px solid ${W.border}`, background:W.bg, marginBottom:'6px' }}>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright }}>{c.name}</div>
                    <div style={{ fontSize:'11px', color:W.muted }}>{billingModelLabel(c.model)}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:'12px', color:W.muted, textDecoration:'line-through' }}>{fmtRate(oldVal)}</div>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:W.accent }}>{fmtRate(newVal)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'8px' }}>{err}</div>}
        </>
      )}
    </div>,
  ];

  const doneContent = (
    <div style={{ textAlign:'center', padding:'12px 0' }}>
      <div style={{ fontSize:'36px', marginBottom:'12px' }}>✅</div>
      <div style={{ fontSize:'16px', fontWeight:'700', color:W.bright, marginBottom:'6px' }}>Rates updated!</div>
      <div style={{ fontSize:'13px', color:W.muted, marginBottom:'20px' }}>{changed.length} client rate{changed.length !== 1 ? 's' : ''} saved successfully.</div>
      <button onClick={onClose}
        style={{ padding:'8px 24px', borderRadius:'7px', border:'none', background:W.accent, color:'#fff', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
        Done
      </button>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:'20px' }}>
      <div style={{ background:W.surface, border:`1px solid ${W.border}`, borderRadius:'14px', width:'100%', maxWidth:'600px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${W.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'17px', fontWeight:'800', color:W.bright }}>Rate Review</div>
              {!done && <div style={{ fontSize:'12px', color:W.muted, marginTop:'2px' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>}
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:W.muted, fontSize:'20px', cursor:'pointer', lineHeight:1, padding:'2px' }}>×</button>
          </div>
          {!done && (
            <div style={{ marginTop:'14px', height:'4px', borderRadius:'2px', background:W.border, overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:'2px', background:W.accent, width:`${((step + 1) / STEPS.length) * 100}%`, transition:'width 0.3s' }} />
            </div>
          )}
        </div>

        <div style={{ padding:'22px 24px', overflowY:'auto', flex:1 }}>
          {done ? doneContent : stepContent[step]}
        </div>

        {!done && step === STEPS.length - 1 && changed.length > 0 && (
          <div style={{ padding:'12px 24px', borderTop:`1px solid ${W.border}`, display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <span style={{ fontSize:'12px', color:W.muted }}>New rates take effect from</span>
            <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)}
              style={{ padding:'6px 10px', borderRadius:'6px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'12px' }} />
            <span style={{ fontSize:'11px', color:W.muted }}>The current rates stay on record for everything before this date.</span>
          </div>
        )}
        {!done && (
          <div style={{ padding:'16px 24px', borderTop:`1px solid ${W.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            {step > 0
              ? <button onClick={() => { setErr(''); setStep(s => s - 1); }}
                  style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.bright, fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>← Back</button>
              : <div />
            }
            {step < STEPS.length - 1
              ? <button onClick={() => { setErr(''); setStep(s => s + 1); }} disabled={step === 1 && loading}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                    background: (step === 1 && loading) ? W.border : W.accent,
                    color: (step === 1 && loading) ? W.muted : '#fff',
                    cursor: (step === 1 && loading) ? 'default' : 'pointer' }}>
                  Continue →
                </button>
              : <button onClick={saveChanges} disabled={saving}
                  style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                    background: saving ? W.border : W.accent, color: saving ? W.muted : '#fff', cursor: saving ? 'default' : 'pointer' }}>
                  {saving ? 'Saving…' : changed.length === 0 ? 'Close' : `Save ${changed.length} Change${changed.length !== 1 ? 's' : ''}`}
                </button>
            }
          </div>
        )}
      </div>
    </div>
  );
}
